/**
 * Permission implication table (S0.2) — the declarative "grant X ⇒ also grant Y"
 * expansion the rbacV2 resolver applies AFTER the Axis-A role-closure union
 * (plan §Slice 0.2 · thesis "expanded by an implication table
 * `products.manage ⇒ products.view`").
 *
 * Generalizes fleet's `PERMISSION_ID_ALIASES` into two forward-only rules:
 *   1. every `X.manage` implies `X.view`            (manage ⇒ view)
 *   2. `governance.manage` implies EVERY `*.view`    (governance.manage ⇒ *.view)
 *   3. `finance.costBasis.edit` implies `finance.view` (edit ⇒ its module view)
 *
 * The table is DERIVED from the frozen `ALL_PERMISSIONS` catalog so it can never
 * drift from it. It is **idempotent** (closing a closed set adds nothing) and
 * **acyclic** (every target is a leaf `*.view` code that is never itself a key) —
 * both asserted in the S0.2 vitest. Implication targets need NOT be catalogued
 * grantable codes: `products.view` etc. are "phantom" view permissions the
 * resolver materialises but the flat catalog does not list (exactly as the plan's
 * `products.manage → [products.view]` example intends).
 *
 * Purity: zero I/O, zero React/MSW, no `Date.now`/`Math.random` — same contract
 * as `src/lib/rbac/*`. Only ADDS grants, so it can never narrow access (the
 * no-lockout invariant is safe by construction).
 */

import { ALL_PERMISSIONS, FINANCE_COST_BASIS_EDIT } from './routePermissions';

const MANAGE_SUFFIX = '.manage';
const VIEW_SUFFIX = '.view';

/** `governance.manage` is special-cased to imply ALL views, not `governance.view`. */
const GOVERNANCE_MANAGE = 'governance.manage';

/** The `X.view` form of an `X.manage` code (`products.manage` → `products.view`). */
function viewFormOfManage(manageCode: string): string {
  return `${manageCode.slice(0, -MANAGE_SUFFIX.length)}${VIEW_SUFFIX}`;
}

/** Every `X.manage` (except governance.manage) → its `X.view` sibling. */
const manageToViewEntries: ReadonlyArray<readonly [string, readonly string[]]> =
  ALL_PERMISSIONS.filter(
    (code) => code.endsWith(MANAGE_SUFFIX) && code !== GOVERNANCE_MANAGE
  ).map((code) => [code, [viewFormOfManage(code)]] as const);

/**
 * The union of every module's view form — the real catalogued `*.view` codes
 * plus each manage-derived phantom view. `governance.manage` implies all of them.
 */
const allViewCodes: readonly string[] = Array.from(
  new Set<string>([
    ...ALL_PERMISSIONS.filter((code) => code.endsWith(VIEW_SUFFIX)),
    ...manageToViewEntries.map(([, views]) => views[0]),
  ])
);

/**
 * The declarative implication map: permission code → the codes it DIRECTLY
 * implies. `resolvePermissionClosure` expands it reflexive-transitively.
 */
export const PERMISSION_IMPLICATIONS: Readonly<
  Record<string, readonly string[]>
> = Object.freeze({
  ...Object.fromEntries(manageToViewEntries),
  // governance.manage ⇒ *.view (the coarse "sees every module" grant)
  [GOVERNANCE_MANAGE]: allViewCodes,
  // the dedicated cost-basis edit needs finance visibility (edit ⇒ module view)
  [FINANCE_COST_BASIS_EDIT]: ['finance.view'],
});

/**
 * The reflexive-transitive closure of `codes` under `implications`.
 *
 * Returns a set containing every input code PLUS every code reachable through
 * the implication edges. Cycle-safe (a visited-guard terminates any cycle) and
 * therefore idempotent — `resolvePermissionClosure(resolvePermissionClosure(x))`
 * equals `resolvePermissionClosure(x)`.
 */
export function resolvePermissionClosure(
  codes: Iterable<string>,
  implications: Readonly<Record<string, readonly string[]>> = PERMISSION_IMPLICATIONS
): Set<string> {
  const closed = new Set<string>();
  const stack: string[] = [...codes];
  while (stack.length > 0) {
    const code = stack.pop() as string;
    if (closed.has(code)) continue; // visited-guard ⇒ cycle-safe + idempotent
    closed.add(code);
    const implied = implications[code];
    if (implied) {
      for (const next of implied) {
        if (!closed.has(next)) stack.push(next);
      }
    }
  }
  return closed;
}

/**
 * The codes that `code` DIRECTLY implies (one hop, not transitive). Empty when
 * `code` implies nothing. Useful for the matrix "via implication of {code}"
 * tooltip source (S2.2).
 */
export function directImplications(
  code: string,
  implications: Readonly<Record<string, readonly string[]>> = PERMISSION_IMPLICATIONS
): readonly string[] {
  return implications[code] ?? [];
}

/**
 * Returns the first implication cycle found (as the node path that closes the
 * cycle) or `null` when the graph is acyclic. Used by the S0.2 vitest to assert
 * the table is acyclic, and available as a guard if the table ever grows.
 */
export function findImplicationCycle(
  implications: Readonly<Record<string, readonly string[]>> = PERMISSION_IMPLICATIONS
): readonly string[] | null {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const path: string[] = [];

  function visit(node: string): readonly string[] | null {
    color.set(node, GREY);
    path.push(node);
    for (const next of implications[node] ?? []) {
      const c = color.get(next) ?? WHITE;
      if (c === GREY) {
        // back-edge ⇒ cycle: return the path from `next` back to `next`
        return [...path.slice(path.indexOf(next)), next];
      }
      if (c === WHITE) {
        const found = visit(next);
        if (found) return found;
      }
    }
    path.pop();
    color.set(node, BLACK);
    return null;
  }

  for (const node of Object.keys(implications)) {
    if ((color.get(node) ?? WHITE) === WHITE) {
      const found = visit(node);
      if (found) return found;
    }
  }
  return null;
}
